import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserTeamAccess } from '../config/queries'

type ActionableEntity = {
  ids: string[]
  teamReference?: wowaudit.TeamReference
  text: string
  ctaPath?: string
  ctaText: string
  className?: string
}

export const useActionable = (user?: wowaudit.User) => {
  const { t } = useTranslation()

  const storedDismissedActions = JSON.parse(
    localStorage.getItem('dismissedActions') ?? '[]',
  )
  const [dismissedActions, setDismissedActions] = useState<string[]>(
    storedDismissedActions,
  )

  const dismissActionableEntity = (entity: ActionableEntity) => {
    const newDismissedActions = [...dismissedActions, ...entity.ids]
    setDismissedActions(newDismissedActions)
    localStorage.setItem(
      'dismissedActions',
      JSON.stringify(newDismissedActions),
    )
  }

  const actionableCharacters =
    user?.characterReferences?.filter(
      (ch) =>
        ch.actionable &&
        !dismissedActions.includes(`character_${ch.id}_${ch.gdprStatus}`),
    ) ?? []

  const actionableTeams =
    user?.teamReferences?.filter(
      (t) =>
        t.markedForDeletionAt &&
        !dismissedActions.includes(`team_${t.id}_${t.markedForDeletionAt}`),
    ) ?? []

  const { data: accessForTeams } = useUserTeamAccess(
    actionableTeams?.map((t) => t.id) ?? [],
  )

  const actionableTeamsWithAccess = actionableTeams.filter(
    (t) => accessForTeams?.[t.id.toString()],
  )

  const actionableEntities: ActionableEntity[] = [
    ...actionableTeamsWithAccess.map((entity) => ({
      ids: [`team_${entity.id}_${entity.markedForDeletionAt}`],
      teamReference: entity,
      text: t('profile.team-requires-action-warning', {
        name: entity.name,
        owner: entity.ownerReference.name,
      }),
      ctaPath:
        window.location.pathname === `${entity.path}/settings#team`
          ? undefined
          : `${entity.path}/settings#team`,
      ctaText: t('profile.team-settings'),
      className: 'bg-destructive-foreground',
    })),
    ...(actionableCharacters.length
      ? [
          {
            ids: actionableCharacters.map(
              (ch) => `character_${ch.id}_${ch.gdprStatus}`,
            ),
            text: t('profile.character-requires-action-warning'),
            ctaPath:
              window.location.pathname === '/profile/characters'
                ? undefined
                : '/profile/characters',
            ctaText: t('header.profile'),
            className: 'bg-warning-foreground',
          },
        ]
      : []),
  ]

  const [currentActionIndex, setCurrentActionIndex] = useState<number>(0)
  const currentAction: ActionableEntity | undefined =
    actionableEntities[currentActionIndex]

  const hasNextAction = currentActionIndex < actionableEntities.length - 1
  const hasPreviousAction = currentActionIndex > 0

  return {
    actionableEntities,
    currentAction,
    currentActionIndex,
    setCurrentActionIndex,
    hasNextAction,
    hasPreviousAction,
    dismissActionableEntity,
  }
}
